import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import EmojiPickerReact from "emoji-picker-react";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RotateCcw, Upload, Trash2 } from "lucide-react";

export function FakeDataGenerator() {
  const [data, setData] = useState("");
  const [copied, setCopied] = useState(false);

  const gen = () => {
    const fn = ["John", "Jane", "Alice", "Bob", "Charlie", "David", "Emma", "Frank", "Grace", "Henry"];
    const ln = ["Smith", "Doe", "Johnson", "Brown", "Williams", "Miller", "Davis", "Wilson", "Anderson", "Taylor"];
    const depts = ["Engineering", "Marketing", "Sales", "HR", "Legal", "Operations", "Finance"];
    const countries = ["USA", "Canada", "UK", "Germany", "France", "Japan", "Australia"];
    const out = [];
    for(let i=0; i<10; i++) {
      out.push({
        id: 1000 + i + 1,
        name: fn[Math.floor(Math.random()*fn.length)] + " " + ln[Math.floor(Math.random()*ln.length)],
        email: `user${100 + i + 1}@247devhub.io`,
        department: depts[Math.floor(Math.random()*depts.length)],
        country: countries[Math.floor(Math.random()*countries.length)],
        age: Math.floor(Math.random()*35)+22,
        active: Math.random() > 0.25
      });
    }
    setData(JSON.stringify(out, null, 2));
  };

  useEffect(() => {
    gen();
  }, []);

  const handleCopy = () => {
    copyToClipboard(data).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Fake JSON User Data Generator Online"
      description="Create mock JSON database records and user arrays instantly for frontend testing, API development, or spreadsheet validation."
      h1="Fake Data Generator"
      category="Mock Utilities"
      categoryPath="/misc-tools"
      howToUse={[
        "Click the Generate Mock Records button.",
        "The generator populates fully random IDs, names, emails, age levels, and status metrics.",
        "Copy your structured JSON data instantly."
      ]}
      faq={[
        { question: "Is this real user data?", answer: "No, all names, email prefixes, and departments are completely generated randomly using offline dictionaries." },
        { question: "Can I customize the count?", answer: "This tool generates 10 cleanly formatted records on each click for high-speed local testing." }
      ]}
    >
      <div className="space-y-4 max-w-3xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Button onClick={gen} className="bg-slate-900 hover:bg-slate-800 text-white font-medium">Generate New Batch</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!data}
              className="text-xs gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy JSON"}
            </Button>
          </div>
        </div>
        <Textarea value={data} readOnly className="min-h-[350px] font-mono text-xs bg-slate-50 text-slate-700 leading-relaxed shadow-inner border border-slate-200 rounded-xl" />
      </div>
    </ToolPageLayout>
  );
}

export function ImageToBase64() {
  const [b64, setB64] = useState("");
  const [dragging, setDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setB64(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragging(true);
    } else if (e.type === "dragleave") {
      setDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      processFile(file);
    }
  };

  const handleCopy = () => {
    copyToClipboard(b64).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Image to Base64 Encoder - Convert Local Graphics"
      description="Translate your JPG, PNG, WEBP, or SVG image files into standard base64 data strings offline inside your browser RAM instantly."
      h1="Image to Base64 Converter"
      category="Mock Utilities"
      categoryPath="/misc-tools"
      howToUse={[
        "Drag-and-drop your image file into the dashed box or click to select.",
        "The converter will instantly generate an inline data string representation.",
        "Copy your base64 text for CSS, HTML, or web embedding."
      ]}
      faq={[
        { question: "Are my private graphics uploaded?", answer: "No. All conversions are executed offline inside your local sandbox. Your files are never transmitted." },
        { question: "What file types are accepted?", answer: "Any web-compatible image formats including PNG, JPG, GIF, SVG, ICO, and WEBP." }
      ]}
    >
      <div className="space-y-4 max-w-3xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <input 
          type="file" 
          ref={fileInputRef}
          accept="image/*" 
          onChange={onFile} 
          className="hidden" 
        />
        
        <div 
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-150 ${dragging ? "border-indigo-500 bg-indigo-50/50" : "border-slate-300 hover:border-slate-400 bg-slate-50/50"}`}
        >
          <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <span className="text-sm font-semibold text-slate-700 block">Drag-and-drop your image here</span>
          <span className="text-xs text-slate-400 mt-1 block">or click to choose file from local computer</span>
        </div>

        {b64 && (
          <div className="space-y-2 pt-4 border-t border-slate-100">
            <div className="flex justify-between items-center">
              <Label className="text-xs font-bold text-slate-500 uppercase">Base64 Output</Label>
              <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1.5 py-1 h-7">
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy String"}
              </Button>
            </div>
            <Textarea value={b64} readOnly className="min-h-[160px] font-mono text-[10px] text-slate-500 break-all bg-slate-50" />
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function SlugGenerator() {
  const [input, setInput] = useState("Hello World! Welcome to 2026.");
  const [copied, setCopied] = useState(false);
  
  const slug = input.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');

  const handleCopy = () => {
    copyToClipboard(slug).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="URL Slug Generator - Create SEO-Friendly Page Links"
      description="Convert standard display text titles and string headers into clean, lowercase, SEO-optimized URL slug addresses instantly."
      h1="URL Slug Generator"
      category="Mock Utilities"
      categoryPath="/misc-tools"
      howToUse={[
        "Type or paste your display titles in the input field.",
        "The generator automatically forces lowercase, replaces spaces with dashes, and deletes invalid special signs.",
        "Copy your optimized url path link instantly."
      ]}
      faq={[
        { question: "Why generate slugs?", answer: "URL slugs are concise, human-readable keywords that improve search-engine indexing (SEO) and readability." },
        { question: "Does it strip special characters?", answer: "Yes, exclamation points, commas, brackets, and quotes are cleanly removed." }
      ]}
    >
      <div className="space-y-6 max-w-xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold text-slate-700">String to Slugify</Label>
          <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Type title here..." className="h-10 text-sm" />
        </div>
        
        <div className="space-y-2 pt-4 border-t border-slate-100">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold text-slate-500 uppercase">Slug Result</Label>
            <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1.5 py-1 h-7">
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Slug"}
            </Button>
          </div>
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl font-mono text-base text-indigo-600 font-bold text-center break-all">
            {slug || "-"}
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function EmojiPickerTool() {
  const [selected, setSelected] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyToClipboard(selected).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Online Emoji Picker - Select and Copy Emojis"
      description="Quickly browse, search, and copy emojis to your clipboard. Add emojis instantly to your text, social posts, or developer files."
      h1="Emoji Picker"
      category="Mock Utilities"
      categoryPath="/misc-tools"
      howToUse={[
        "Search or browse for emojis using the grid directory.",
        "Click emojis to append them directly into the selection console.",
        "Copy your compiled emojis instantly."
      ]}
      faq={[
        { question: "What format is copied?", answer: "It copies standard native Unicode emoji symbols directly compatible with all devices." },
        { question: "Does it support emoji search?", answer: "Yes, use the search block inside the picker to find emojis instantly by keywords." }
      ]}
    >
      <div className="flex flex-col md:flex-row gap-8 items-start justify-center max-w-4xl mx-auto bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="shrink-0 border border-slate-150 rounded-xl overflow-hidden shadow-xs mx-auto">
          <EmojiPickerReact onEmojiClick={(e) => setSelected(selected + e.emoji)} />
        </div>
        <div className="flex-1 space-y-4 w-full">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-semibold text-slate-700">Selected Emojis Console</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy} disabled={!selected} className="text-xs gap-1.5 py-1 h-7">
                <Copy className="w-3.5 h-3.5" />
                {copied ? "Copied!" : "Copy"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected("")} disabled={!selected} className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5 py-1 h-7">
                <Trash2 className="w-3.5 h-3.5" />
                Clear
              </Button>
            </div>
          </div>
          <Textarea 
            value={selected} 
            onChange={e => setSelected(e.target.value)} 
            placeholder="Click on emojis on the left to copy..." 
            className="text-3xl min-h-[220px] leading-relaxed p-4 bg-slate-50 border border-slate-200 rounded-xl" 
          />
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function MiscTools2() {
  return (
    <ToolPageLayout
      title="Misc Data & Image Tools"
      description="Various tools: Fake data generation, Image to Base64 conversion, Slug generator, and Emoji picker."
      h1="Miscellaneous Tools"
      category="Misc Tools"
      categoryPath="/misc-tools"
      howToUse={[
        "Select the tool you need.",
        "Follow the tool's instructions."
      ]}
      faq={[
        { question: "Are these tools free?", answer: "Yes, all our misc tools are free." }
      ]}
    >
      <div className="space-y-12">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Fake Data Generator</h2><FakeDataGenerator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Image to Base64</h2><ImageToBase64 /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Slug Generator</h2><SlugGenerator /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Emoji Picker</h2><EmojiPickerTool /></section>
      </div>
    </ToolPageLayout>
  );
}
